import React, { useEffect, useContext as useReactContext, useRef } from 'react';

import { RequiredClaim, Student, StudentApi, StudentCourseResult, StudentLoginTarget, StudentSecretQuestion, StudentWithDetails } from 'apis/StudentApi';
import { StudentDetailsContext, StudentsContext } from 'pages/students/context/StudentsDefaultContext';
import { ResetPasswordDialog } from 'components/ResetPasswordDialog/ResetPasswordDialog';
import GMetrixDialogConfirm from 'components/GMetrixDialogConfirm/GMetrixDialogConfirm';
import { GMetrixCheckInput } from 'components/GMetrixCheckInput/GMetrixCheckInput';
import GMetrixDialogShell from 'components/GMetrixDialogShell/GMetrixDialogShell';
import { GroupCodeWrapper } from 'components/GroupCodeWrapper/GroupCodeWrapper';
import { GMetrixDropdown } from 'components/GMetrixDropdown/GMetrixDropdown';
import { GMetrixButton } from 'components/GMetrixButton/GMetrixButton';
import { GMetrixDialog } from 'components/GMetrixDialog/GMetrixDialog';
import { GMetrixLoader } from 'components/GMetrixLoader/GMetrixLoader';
import { GMetrixInput } from 'components/GMetrixInput/GMetrixInput';
import { Alert, Col, Form, Row, Table } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';
import { useNotification } from 'app/notifications';
import { State, StateApi } from 'apis/StateApi';
import { useContext } from 'components/Tabs';
import { Country } from 'apis/CountryApi';
import { getFormattedDate } from 'utils';
import { useSuperState } from 'hooks';
import { useApp } from 'app';

import 'pages/students/components/Details/styles/details.scss';

type LoginTargetOption = { text: string; value: StudentLoginTarget };

/** Dev-only login-target options shown in the feature-gated environment dropdown. */
const LOGIN_TARGETS: LoginTargetOption[] = [
    { text: 'Current Environment', value: 'current' },
    { text: 'Prod', value: 'prod' },
    { text: 'Stage', value: 'stage' },
    { text: 'UAT', value: 'uat' },
    { text: 'Dev', value: 'dev' },
    { text: 'Localhost', value: 'localhost' }
];

interface DetailsProps {
    setStudents: React.Dispatch<React.SetStateAction<Student[]>>;
    studentId: number;
    studentWithDetails?: StudentWithDetails;
    studentWithDetailsOriginal?: StudentWithDetails;
}

interface StudentEditForm {
    accessibility?: boolean;
    betaAccess?: boolean;
    country?: Country;
    email?: string;
    firstName?: string;
    fullName?: string;
    lastName?: string;
    state?: State;
    studentNumber?: string;
    username?: string;
}

/**
 * Ranks a PII claim so partners (e.g. AZROC) that rely on identity fields always see
 * Social Security Number first and Date of Birth second, regardless of how the claim
 * happens to be labelled (SSN, "Social Security Number", "last 4 of s.s.", DOB,
 * "Date of Birth", etc.). All other claims keep their original relative order.
 */
const getPiiClaimPriority = (claim: RequiredClaim): number => {

    const haystack = `${claim?.Name ?? ''} ${claim?.ClaimType ?? ''}`.toLowerCase();

    const isSsn = /\bssn\b|social\s*security|s\.?\s*s\.?\s*n\.?|last\s*4\s*of\s*s|social/.test(haystack);
    if (isSsn) return 0;

    const isDob = /\bdob\b|d\.?\s*o\.?\s*b\.?|date\s*of\s*birth|birth\s*date|birthday|\bbirth\b/.test(haystack);
    if (isDob) return 1;

    return 2;

};

/**
 * Returns the PII claims ordered so SSN and DOB surface first. Uses a stable sort so
 * claims sharing a priority retain their server-provided order.
 */
const orderPiiClaims = (claims: RequiredClaim[]): RequiredClaim[] => [...claims].sort((a, b) => getPiiClaimPriority(a) - getPiiClaimPriority(b));

/**
 * Masks a Social Security Number so only the last four digits are visible
 * (e.g. "123-45-6789" → "•••-••-6789"). Values with four or fewer digits (such as a
 * "last 4 of SSN" field) are returned unchanged.
 */
const maskSsnValue = (value: string): string => {

    const digits = value.replace(/\D/g, '');
    if (digits.length <= 4) return value;
    return `•••-••-${digits.slice(-4)}`;

};

/**
 * Formats a compact, all-digit date of birth (e.g. "12211989" or "19891221") as an
 * MM/DD/YYYY string. Returns null when the digits do not form a plausible date so the
 * caller can fall back to the raw value.
 */
const formatCompactDob = (digits: string): string | null => {

    if (digits.length !== 8) return null;

    const currentYear = new Date().getFullYear();
    const first4 = parseInt(digits.slice(0, 4), 10);
    const last4 = parseInt(digits.slice(4, 8), 10);

    let year: number;
    let month: number;
    let day: number;

    if (first4 >= 1900 && first4 <= currentYear) {

        // YYYYMMDD
        year = first4;
        month = parseInt(digits.slice(4, 6), 10);
        day = parseInt(digits.slice(6, 8), 10);

    } else if (last4 >= 1900 && last4 <= currentYear) {

        // MMDDYYYY
        month = parseInt(digits.slice(0, 2), 10);
        day = parseInt(digits.slice(2, 4), 10);
        year = last4;

    } else {

        return null;

    }

    if (month < 1 || month > 12 || day < 1 || day > 31) return null;

    const parsed = new Date(year, month - 1, day);
    return isNaN(parsed.getTime()) ? null : getFormattedDate(parsed);

};

/**
 * Parses a stored date-of-birth value (compact "12211989"/"19891221" digits, ISO, or any
 * Date-parseable string) into its calendar parts, or null when it is not a valid date.
 */
const parseDobParts = (value: string): { day: number; month: number; year: number } | null => {

    const v = (value ?? '').trim();
    if (v === '') return null;

    if (/^\d{8}$/.test(v)) {

        const currentYear = new Date().getFullYear();
        const first4 = parseInt(v.slice(0, 4), 10);
        const last4 = parseInt(v.slice(4, 8), 10);

        if (first4 >= 1900 && first4 <= currentYear) {

            return { day: parseInt(v.slice(6, 8), 10), month: parseInt(v.slice(4, 6), 10), year: first4 };

        }

        if (last4 >= 1900 && last4 <= currentYear) {

            return { day: parseInt(v.slice(2, 4), 10), month: parseInt(v.slice(0, 2), 10), year: last4 };

        }

        return null;

    }

    const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(v);
    const parsed = new Date(isoDateOnly ? `${v}T00:00:00` : v);
    if (isNaN(parsed.getTime())) return null;

    return { day: parsed.getDate(), month: parsed.getMonth() + 1, year: parsed.getFullYear() };

};

/**
 * Converts a stored date-of-birth value into the ISO "YYYY-MM-DD" string required by an
 * HTML date input. Returns an empty string when the value is not a recognisable date.
 */
const dobToIsoInput = (value: string): string => {

    const parts = parseDobParts(value);
    if (!parts) return '';

    return `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`;

};

/**
 * Converts an ISO "YYYY-MM-DD" value (from a date input) back into the same storage format
 * as the original value, so editing a compact "MMDDYYYY" DOB keeps that shape.
 */
const isoInputToStoredDob = (iso: string, originalValue: string): string => {

    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
    if (!match) return iso;

    const [, year, month, day] = match;
    const original = (originalValue ?? '').trim();

    if (/^\d{8}$/.test(original)) {

        const currentYear = new Date().getFullYear();
        const first4 = parseInt(original.slice(0, 4), 10);

        // Preserve the original compact ordering (YYYYMMDD vs MMDDYYYY).
        return (first4 >= 1900 && first4 <= currentYear) ? `${year}${month}${day}` : `${month}${day}${year}`;

    }

    return `${month}/${day}/${year}`;

};

/**
 * Formats a PII claim value for display. SSN claims reveal only their last four digits,
 * date-of-birth claims are rendered as an actual date (MM/DD/YYYY), and everything else
 * (including values that cannot be parsed) is returned unchanged.
 */
const formatPiiClaimValue = (claim: RequiredClaim): string => {

    const value = (claim?.Value ?? '').trim();
    if (value === '') return value;

    const priority = getPiiClaimPriority(claim);

    // Social Security Number → reveal only the last four digits.
    if (priority === 0) return maskSsnValue(value);

    // Date of birth → render as an actual date.
    if (priority === 1) {

        // Compact, all-digit dates (e.g. "12211989") are not parseable by Date, so
        // convert them to an MM/DD/YYYY string first.
        if (/^\d{8}$/.test(value)) {

            const compact = formatCompactDob(value);
            if (compact) return compact;

        }

        // Treat ISO date-only strings as local time to avoid an off-by-one day from
        // UTC parsing; other formats (e.g. MM/DD/YYYY) already parse as local.
        const isoDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
        const parsed = new Date(isoDateOnly ? `${value}T00:00:00` : value);

        return isNaN(parsed.getTime()) ? value : getFormattedDate(parsed);

    }

    return value;

};

export function Details(props: DetailsProps) {

    const { setStudents, studentId, studentWithDetails } = props;

    const [state, setState] = useSuperState({
        courseResults: [] as StudentCourseResult[],
        courseResultsLoading: false,
        error: null as string,
        formDetails: {} as StudentEditForm,
        loading: false,
        loginTarget: 'current' as StudentLoginTarget,
        openModal: false,
        piiClaims: [] as RequiredClaim[],
        piiFormValues: {} as Record<string, string>,
        piiLoading: false,
        secretQuestion: null as StudentSecretQuestion | null,
        showEditModal: false,
        states: [] as State[],
        usernameStatus: 'idle' as 'idle' | 'checking' | 'available' | 'taken'
    });
    const { courseResults, courseResultsLoading, error, formDetails, loading, loginTarget, openModal, piiClaims, piiFormValues, piiLoading, secretQuestion, showEditModal, states, usernameStatus } = state;
    const usernameDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const { formatMessage } = useIntl();
    const { highestRole, isFeatureAllowed } = useApp();
    const { countries, permissions } = useReactContext(StudentsContext);
    const { open, pageContext, update, updateById } = useContext(StudentDetailsContext);
    const { catchWithToast, createToast } = useNotification();
    const gmetrixConfirmRef = useRef<any>();
    const gmetrixDialogShellRef = useRef<any>();

    const isSysAdmin = highestRole?.id === 1;

    // Dev-only affordance: choosing which environment's student panel to ghost into is hidden
    // behind the Development feature ring. Gate on ring membership (isFeatureAllowed) to match the
    // backend's allowed-only CheckFeatureFlag — not the user's early-access opt-in preference.
    const devLoginEnabled = isFeatureAllowed('Admin.Students.DevLoginAsStudent');

    const clearError = () => setState({ error: null });

    const setFormDetails = (formDetails: StudentEditForm) => setState({ formDetails });

    const editStudentDetailsOnClick = async () => {

        if (!studentWithDetails) return;

        try {

            setState({ loading: true });

            await toggleStudentBetaAccess();

            await StudentApi.Update({
                ...studentWithDetails,
                Country: formDetails?.country,
                Email: formDetails?.email ?? '',
                FeatureRingId: formDetails?.betaAccess ? 0 : 20,
                Firstname: formDetails?.firstName ?? '',
                Fullname: formDetails?.fullName ?? '',
                IsAda: formDetails?.accessibility ?? false,
                Lastname: formDetails?.lastName ?? '',
                State: formDetails?.state,
                StudentNumber: formDetails?.studentNumber ?? '',
                Username: formDetails?.username ?? studentWithDetails.Username
            });

            await savePiiClaims();

            handleStudentUpdate();

            const students = await StudentApi.GetAll();
            setStudents(students);

            setState({ loading: false });

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const handleStudentUpdate = () => {

        if (studentWithDetails) {

            const studentObj = {
                ...studentWithDetails,
                Country: formDetails?.country,
                Email: formDetails?.email ?? '',
                FeatureRingId: formDetails?.betaAccess ? 20 : 0,
                Firstname: formDetails?.firstName ?? '',
                Fullname: formDetails?.fullName ?? '',
                IsAda: formDetails?.accessibility ?? false,
                Lastname: formDetails?.lastName ?? '',
                State: formDetails?.state,
                StudentNumber: formDetails?.studentNumber ?? '',
                Username: formDetails?.username ?? studentWithDetails.Username
            };
            open({
                Data: { studentWithDetails: studentObj, studentWithDetailsOriginal: studentObj },
                HasChanges: false,
                Id: studentWithDetails.Id,
                Page: 'Students',
                Title: `${formDetails?.firstName} ${formDetails?.lastName}`
            });

        }
        createToast(formatMessage({ id: 'account_name_has_been_updated.cf8a163f', defaultMessage: 'Account {name} has been updated.' }, { name: `${studentWithDetails?.Firstname} ${studentWithDetails?.Lastname}` }), 'success');
        setState({ showEditModal: false });

    };

    const editFullName = (value: string, firstOrLast: 'firstName' | 'lastName') => {

        const splitFullName = studentWithDetails?.Fullname?.split(' ');
        let newFullNameAsString;
        if (firstOrLast === 'firstName') {

            newFullNameAsString = `${value} ${splitFullName && splitFullName[splitFullName.length - 1]}`;

        } else {

            newFullNameAsString = `${splitFullName && splitFullName[0]} ${value}`;

        }
        return newFullNameAsString;

    };

    const loadPiiClaims = async () => {

        try {

            setState({ piiLoading: true });

            const piiClaims = await StudentApi.GetStudentClaims(studentId);

            const piiFormValues: Record<string, string> = {};
            piiClaims.forEach(claim => {

                piiFormValues[claim.ClaimType] = claim.Value ?? '';

            });

            setState({ piiClaims, piiFormValues, piiLoading: false });

        } catch (e) {

            setState({ piiLoading: false });
            catchWithToast(formatMessage({ id: 'failed_to_load_pii_details.0125d325', defaultMessage: 'Failed to load PII details.' }))(e);

        }

    };

    const savePiiClaims = async () => {

        if (!permissions.EditPII) return;

        const changedClaims = piiClaims
            .filter(claim => (piiFormValues[claim.ClaimType] ?? '') !== (claim.Value ?? ''))
            .map(claim => ({ ...claim, Value: piiFormValues[claim.ClaimType] ?? '' }));

        if (changedClaims.length === 0) return;

        await StudentApi.UpdateStudentPiiClaims(studentId, changedClaims);
        await loadPiiClaims();

    };

    const loadCourseResults = async () => {

        try {

            setState({ courseResultsLoading: true });

            const courseResults = await StudentApi.GetStudentCourseResults(studentId);

            setState({ courseResults, courseResultsLoading: false });

        } catch (e) {

            setState({ courseResultsLoading: false });
            catchWithToast(formatMessage({ id: 'failed_to_load_exam_results.95e90a64', defaultMessage: 'Failed to load exam results.' }))(e);

        }

    };

    const loginAsStudent = async () => {

        if (!studentWithDetails?.Id) return;

        try {

            setState({ loading: true });

            const bridgeUrl = await StudentApi.GetStudentLoginBridgeUrl(studentWithDetails.Id, loginTarget);
            window.open(bridgeUrl, '_blank');

            setState({ loading: false });

        } catch (e) {

            setState({ loading: false });
            catchWithToast(formatMessage({ id: 'failed_to_log_in_as_student.f356d308', defaultMessage: 'Failed to log in as student.' }))(e);

        }

    };

    const toggleStudentBetaAccess = async () => {

        if (!studentWithDetails) return;

        try {

            setState({ loading: true });

            const result = await StudentApi.ToggleStudentBetaRing(studentWithDetails.Id, formDetails?.betaAccess ? 20 : 0);

            if (result?.Done) {

                updateById(studentWithDetails.Id, {
                    studentWithDetails: {
                        ...studentWithDetails, FeatureRingId: result?.Id ?? 0
                    }
                });

            }

            setState({ loading: false });

        } catch (e){

            setState({
                error: e?.message,
                loading: false
            });

        }

    };

    const setArchiveStatusStudent = async (studentId: number, archiveStatus: boolean) => {

        try {

            setState({ loading: true });

            const result = await StudentApi.SetArchiveStudentsTestingCenter([studentId], archiveStatus);
            if (!result.Done) throw new Error(result.Message);

            const message = archiveStatus
                ? formatMessage({ id: 'archived_student_successfully.9e01593e', defaultMessage: 'Archived student successfully' })
                : formatMessage({ id: 'restored_student_successfully.85a0e64f', defaultMessage: 'Restored student successfully' });

            createToast(message, 'success');

            if (pageContext?.Data.studentWithDetails) {

                update({
                    studentWithDetails: {
                        ...pageContext.Data.studentWithDetails,
                        Archived: archiveStatus
                    },
                    studentWithDetailsOriginal: {
                        ...pageContext.Data.studentWithDetails,
                        Archived: archiveStatus
                    }
                });

            }

            setState({ loading: false });

        } catch (e){

            setState({
                error: e?.message || e,
                loading: false
            });

        }

    };

    const verifyInput = () => {

        let errorFound = false;
        if (formDetails?.firstName?.length === 0) {

            errorFound = true;

        }

        if (!formDetails?.country?.Id) {

            errorFound = true;

        }

        if (permissions.EditUsername) {

            if (!formDetails?.username || formDetails.username.length === 0) {

                errorFound = true;

            } else if (usernameStatus === 'checking' || usernameStatus === 'taken') {

                errorFound = true;

            }

        }

        return errorFound;

    };

    const StudentForm = () => (
        <Form key={'studentForm'} >

            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput
                        required
                        disabled={loading}
                        isInvalid={formDetails?.firstName?.length === 0}
                        label={formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' })}
                        onChange={e => setFormDetails({ ...formDetails, firstName: e.target.value, fullName: editFullName(e.target.value, 'firstName') })}
                        placeholder={formatMessage({ id: 'first_name.759a675b', defaultMessage: 'First Name' })}
                        type="text"
                        value={formDetails?.firstName}
                    />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentLName" md={4}>
                    <GMetrixInput
                        required
                        disabled={loading}
                        isInvalid={formDetails?.lastName?.length === 0}
                        label={formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' })}
                        onChange={e => setFormDetails({ ...formDetails, fullName: editFullName(e.target.value, 'lastName'), lastName: e.target.value })}
                        placeholder={formatMessage({ id: 'last_name.029e7273', defaultMessage: 'Last Name' })}
                        type="text"
                        value={formDetails?.lastName}
                    />
                </Form.Group>
            </Row>

            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentEmail" md={4}>
                    <GMetrixInput
                        required
                        disabled={loading}
                        isInvalid={formDetails?.email?.length === 0}
                        label={formatMessage({ id: 'email.500d9e86', defaultMessage: 'Email' })}
                        onChange={e => setFormDetails({ ...formDetails, email: e.target.value })}
                        placeholder={formatMessage({ id: 'email.500d9e86', defaultMessage: 'Email' })}
                        type="text"
                        value={formDetails?.email}
                    />
                </Form.Group>
                {permissions.EditUsername && (
                    <Form.Group as={Col} className="mt-2" controlId="formStudentUsername" md={4}>
                        <GMetrixInput
                            required
                            feedbackMessage={
                                !formDetails?.username || formDetails.username.length === 0
                                    ? formatMessage({ id: 'username_is_required.f0703673', defaultMessage: 'Username is required' })
                                    : usernameStatus === 'taken'
                                        ? formatMessage({ id: 'username_already_exists.f787ffc8', defaultMessage: 'Username already exists' })
                                        : usernameStatus === 'checking'
                                            ? formatMessage({ id: 'checking_availability.f6644061', defaultMessage: 'Checking availability…' })
                                            : usernameStatus === 'available'
                                                ? formatMessage({ id: 'username_is_available.1f71fc7b', defaultMessage: 'Username is available' })
                                                : ''
                            }
                            validType={
                                !formDetails?.username || formDetails.username.length === 0 || usernameStatus === 'taken'
                                    ? 'invalid'
                                    : usernameStatus === 'available'
                                        ? 'valid'
                                        : undefined
                            }
                            disabled={loading}
                            label={formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' })}
                            onChange={e => setFormDetails({ ...formDetails, username: e.target.value.trim() })}
                            placeholder={formatMessage({ id: 'username.dceab582', defaultMessage: 'Username' })}
                            type="text"
                            value={formDetails?.username}
                        />
                    </Form.Group>
                )}
            </Row>

            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentEmail" md={4}>
                    <GMetrixDropdown
                        required
                        dataItemKey="Id"
                        dataItems={countries}
                        disabled={loading || !permissions.Edit}
                        label={formatMessage({ id: 'country.05543f81', defaultMessage: 'Country', description: 'New' })}
                        onChange={e => setFormDetails({ ...formDetails, country: e })}
                        textField="Name"
                        value={formDetails?.country}
                    />
                </Form.Group>
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixDropdown
                        required
                        dataItemKey="Id"
                        dataItems={states ?? []}
                        disabled={loading || !permissions.Edit}
                        label={formatMessage({ id: 'stateprovince.d2dfa3c8', defaultMessage: 'State/Province' })}
                        onChange={e => setFormDetails({ ...formDetails, state: e })}
                        textField="Name"
                        value={formDetails?.state}

                    />
                </Form.Group>
            </Row>

            <Row className="mb-2">
                <Form.Group as={Col} className="mt-2" controlId="formStudentFName" md={4}>
                    <GMetrixInput
                        disabled={loading}
                        label={formatMessage({ id: 'student_number.4ed64306', defaultMessage: 'Student Number' })}
                        onChange={e => setFormDetails({ ...formDetails, studentNumber: e.target.value })}
                        placeholder={formatMessage({ id: 'student_number.4ed64306', defaultMessage: 'Student Number' })}
                        type="text"
                        value={formDetails?.studentNumber}
                    />
                </Form.Group>

                <Form.Group as={Col} className="mt-2  d-flex align-items-end" controlId="formStudentEmail" md={4}>
                    <GMetrixCheckInput
                        checked={formDetails?.accessibility}
                        disabled={loading || !permissions.Edit}
                        label={formatMessage({ id: 'accessibility.0c8f244d', defaultMessage: 'Accessibility' })}
                        onChange={e => setFormDetails({ ...formDetails, accessibility: e.target.checked })}
                        type="switch"

                    />
                </Form.Group>
                {pageContext?.Data.studentWithDetails && pageContext?.Data.studentWithDetails?.FeatureRingId <= 20 && (
                <Form.Group as={Col} className="mt-2 d-flex align-items-end" controlId="formStudentFName" md={4}>

                    <GMetrixCheckInput
                        checked={formDetails?.betaAccess}
                        disabled={loading}
                        label={formatMessage({ id: 'beta_access.367c484f', defaultMessage: 'Beta Access' })}
                        onChange={e => setFormDetails({ ...formDetails, betaAccess: e.target.checked })}
                        type="switch"
                    />
                </Form.Group>
                )}
            </Row>

            {permissions.EditPII && piiClaims.length > 0 && (
                <>
                    <hr />
                    <Row className="mb-2">
                        {orderPiiClaims(piiClaims).map(claim => {

                            const priority = getPiiClaimPriority(claim);
                            const isDob = priority === 1;
                            const isSsn = priority === 0;
                            const storedValue = piiFormValues[claim.ClaimType] ?? '';

                            return (
                                <Form.Group as={Col} className="mt-2" controlId={`formPii_${claim.ClaimType}`} key={claim.ClaimType} md={4}>
                                    <GMetrixInput
                                        onChange={e => setState({
                                            piiFormValues: {
                                                ...piiFormValues,
                                                [claim.ClaimType]: isDob
                                                    ? isoInputToStoredDob(e.target.value, storedValue)
                                                    : e.target.value
                                            }
                                        })}
                                        disabled={loading || isSsn}
                                        label={claim.Name}
                                        placeholder={claim.Name}
                                        type={isDob ? 'date' : 'text'}
                                        value={isSsn ? maskSsnValue(storedValue) : isDob ? dobToIsoInput(storedValue) : storedValue}
                                    />
                                </Form.Group>
                            );

                        })}
                    </Row>
                </>
            )}

        </Form>
    );

    useEffect(() => {

        if (!studentWithDetails) {

            const loadStudent = async () => {

                try {

                    const studentWithDetails = await StudentApi.Get(studentId);
                    updateById(studentId, { studentWithDetails });

                } catch (e) {

                    catchWithToast(formatMessage({ id: 'failed_to_load_student_details.28f34fe4', defaultMessage: 'Failed to load student details.' }))(e);

                }

            };
            loadStudent();

        }

    }, [studentWithDetails, studentId]);

    useEffect(() => {

        if (!isSysAdmin || !studentId) return;

        const loadSecretQuestion = async () => {

            try {

                const secretQuestion = await StudentApi.GetSecretQuestion(studentId);
                setState({ secretQuestion });

            } catch (e) {

                setState({ secretQuestion: null });
                catchWithToast(formatMessage({ id: 'failed_to_load_secret_question.eb68b5e3', defaultMessage: 'Failed to load secret question.' }))(e);

            }

        };
        loadSecretQuestion();

    }, [isSysAdmin, studentId]);

    useEffect(() => {

        const details = studentWithDetails;
        setFormDetails({
            accessibility: details?.IsAda ?? false,
            betaAccess: details && details?.FeatureRingId > 0,
            country: details?.Country,
            email: details?.Email,
            firstName: details?.Firstname,
            fullName: details?.Fullname,
            lastName: details?.Lastname,
            state: details?.State,
            studentNumber: details?.StudentNumber,
            username: details?.Username
        });

    }, [studentWithDetails]);

    useEffect(() => {

        if (!permissions.EditUsername) return;

        if (usernameDebounceRef.current) {

            clearTimeout(usernameDebounceRef.current);

        }

        const nextUsername = formDetails?.username ?? '';
        const originalUsername = studentWithDetails?.Username ?? '';

        // Empty or unchanged → no lookup needed.
        if (!nextUsername || nextUsername.toLowerCase() === originalUsername.toLowerCase()) {

            setState({ usernameStatus: 'idle' });
            return;

        }

        setState({ usernameStatus: 'checking' });

        usernameDebounceRef.current = setTimeout(async () => {

            try {

                const isAvailable = await StudentApi.ValidateUsername(nextUsername);
                setState({ usernameStatus: isAvailable ? 'available' : 'taken' });

            } catch {

                setState({ usernameStatus: 'idle' });

            }

        }, 400);

        return () => {

            if (usernameDebounceRef.current) {

                clearTimeout(usernameDebounceRef.current);

            }

        };

    }, [formDetails?.username, studentWithDetails?.Username, permissions.EditUsername]);

    useEffect(() => {

        if (formDetails?.country?.Id) {

            const loadStates = async () => {

                try {

                    const states = await StateApi.GetAll(formDetails.country.Id);

                    // New Students created through OAuth have State being optional so I auto populating their input.
                    const optionalDefaultState = states.findIndex(state => state.Id === 462);
                    if (studentWithDetails) {

                        setState({ states });
                        setFormDetails({
                            ...formDetails, state: states.length === 1
                                ? states[0]
                                : studentWithDetails.State ?? states[optionalDefaultState]
                        });

                    }

                } catch (e) {

                    catchWithToast(formatMessage({ id: 'failed_to_load_states_for_selected_count.8e0c654c', defaultMessage: 'Failed to load states for selected country.' }))(e);

                }

            };
            loadStates();

        }

    }, [formDetails?.country?.Id]);

    useEffect(() => {

        if (studentId && (permissions.ViewPII || permissions.EditPII)) {

            loadPiiClaims();

        }

        if (studentId && permissions.ViewPII) {

            loadCourseResults();

        }

    }, [permissions.ViewPII, permissions.EditPII, studentId]);

    return (
        <>
            {!studentWithDetails ?
                (
                    <div className="d-flex justify-content-center align-items-center p-5">
                        <GMetrixLoader />
                    </div>
                )
                :
                (
                    <>

                        <Row className={'gap-3'}>

                            <div className="d-flex flex-wrap justify-content-md-between">
                                <div>
                                    <GroupCodeWrapper
                                        archived={studentWithDetails?.Archived}
                                        showCode={false}
                                    />
                                </div>

                                <div className="d-flex flex-wrap gap-1 justify-content-sm-end">
                                    {studentWithDetails?.Archived ?
                                        (
                                            <GMetrixButton
                                                onClick={async e => {

                                                    const dontRemindMeRestoreStudent = sessionStorage.getItem('dontRemindMeRestoreStudent');

                                                    if (dontRemindMeRestoreStudent && JSON.parse(dontRemindMeRestoreStudent) && pageContext) {

                                                        // just restore the classroom without reminded modal
                                                        setArchiveStatusStudent(pageContext?.Id, false);
                                                        return;

                                                    }

                                                    const [success, dontRemindMe] = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'restore_student.50a5d884', defaultMessage: 'Restore Student' }),
                                                        <>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Restoring a Student will:" id="restoring_a_student_will.6d59691a" />
                                                            </h6>
                                                            <ul>
                                                                <li>
                                                                    <FormattedMessage defaultMessage="Restore students access to content assigned through classrooms and access codes." id="restore_students_access_to_content_assig.3db23685" />
                                                                </li>
                                                            </ul>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Do you wish to continue?" id="do_you_wish_to_continue.b5c0f89c" />
                                                            </h6>

                                                        </>,
                                                        { buttonText: formatMessage({ id: 'restore_student.50a5d884', defaultMessage: 'Restore Student' }), iconClass: 'fa-regular fa-undo', variant: 'primary' },
                                                        { label: formatMessage({ id: 'dont_show_again.17a8f957', defaultMessage: 'Don\'t show again' }) }
                                                    );

                                                    if (success && pageContext) {

                                                        //restore the classroom
                                                        setArchiveStatusStudent(pageContext?.Id, false);

                                                    }

                                                    // sets boolean value to block modal from appearing during the same session
                                                    if (dontRemindMe) {

                                                        sessionStorage.setItem('dontRemindMeRestoreStudent', JSON.stringify(true));

                                                    }

                                                }}
                                                disabled={permissions!.PartnerType !== 4}
                                                iconClass="fa-regular fa-undo"
                                                infoTitle={formatMessage({ id: 'archiving_is_not_available_for_this_part.6ca7b66d', defaultMessage: 'Archiving is not available for this partner type.' })}
                                                variant="outline-primary"
                                            >
                                                <FormattedMessage defaultMessage="Restore" id="restore.d528bbad" />
                                            </GMetrixButton>
                                        )
                                        :
                                        (
                                            <GMetrixButton
                                                onClick={async e => {

                                                    const dontRemindMeRestoreStudent = sessionStorage.getItem('dontRemindMeRestoreStudent');

                                                    if (dontRemindMeRestoreStudent && JSON.parse(dontRemindMeRestoreStudent) && pageContext) {

                                                        // just archive the classroom without reminded modal
                                                        setArchiveStatusStudent(pageContext?.Id, true);
                                                        return;

                                                    }

                                                    const [success, dontRemindMe] = await gmetrixConfirmRef.current.confirm(formatMessage({ id: 'archive_student.ba61d24e', defaultMessage: 'Archive Student' }),
                                                        <>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Archiving a Student will:" id="archiving_a_student_will.e35ffc7c" />
                                                            </h6>
                                                            <ul>
                                                                <li>
                                                                    <FormattedMessage defaultMessage="Remove students access to content assigned through classrooms and access codes." id="remove_students_access_to_content_assign.92a994d3" />
                                                                </li>
                                                            </ul>
                                                            <h6>
                                                                <FormattedMessage defaultMessage="Do you wish to continue?" id="do_you_wish_to_continue.b5c0f89c" />
                                                            </h6>

                                                        </>,
                                                        { buttonText: formatMessage({ id: 'archive_student.ba61d24e', defaultMessage: 'Archive Student' }), iconClass: 'fa-regular fa-cabinet-filing', variant: 'warning' },
                                                        { label: formatMessage({ id: 'dont_show_again.17a8f957', defaultMessage: 'Don\'t show again' }) }
                                                    );

                                                    if (success && pageContext) {

                                                        //archive the classroom
                                                        setArchiveStatusStudent(pageContext?.Id, true);

                                                    }

                                                    // sets boolean value to block modal from appearing during the same session
                                                    if (dontRemindMe) {

                                                        sessionStorage.setItem('dontRemindMeRestoreStudent', JSON.stringify(true));

                                                    }

                                                }}
                                                disabled={permissions && permissions.PartnerType !== 4}
                                                iconClass="fa-regular fa-cabinet-filing"
                                                variant="outline-primary"
                                            >
                                                <FormattedMessage defaultMessage="Archive" id="archive.61ce1f3a" />
                                            </GMetrixButton>
                                        )
                                    }
                                    <GMetrixButton
                                        iconClass="fa-arrows-rotate fa-solid"
                                        onClick={() => setState({ openModal: true })}
                                        variant="outline-primary"
                                    >
                                        <FormattedMessage defaultMessage="Reset Password" id="reset_password.9c0200aa" />

                                    </GMetrixButton>

                                    {!!permissions?.Login && (
                                        <>
                                            {devLoginEnabled && (
                                            <div className="me-2" style={{ width: 190 }}>
                                                <GMetrixDropdown
                                                    dataItemKey="value"
                                                    dataItems={LOGIN_TARGETS}
                                                    filterable={false}
                                                    iconClass="fa-solid fa-server"
                                                    infoTitle={formatMessage({ id: 'dev_only_choose_which_environments_stude.0c0d8750', defaultMessage: 'Dev only: choose which environment\'s student panel to open. Defaults to the current environment.' })}
                                                    onChange={item => setState({ loginTarget: (item as LoginTargetOption).value })}
                                                    value={LOGIN_TARGETS.find(option => option.value === loginTarget)}
                                                />
                                            </div>
                                            )}
                                            <GMetrixButton className="me-3" onClick={loginAsStudent} type="button" variant="outline-primary">
                                                <FormattedMessage defaultMessage="Login as Student" id="login_as_student.cd8963a8" />
                                            </GMetrixButton>
                                        </>
                                    )}

                                    <GMetrixButton
                                        className="float-right"
                                        iconClass="fa-pencil fa-solid"
                                        onClick={() => setState({ showEditModal: true })}
                                        variant="primary"
                                    >
                                        <FormattedMessage defaultMessage="Edit" id="edit.00978daf" />
                                    </GMetrixButton>
                                </div>
                            </div>

                            <Col md={8}>

                                <Row as="dl" className="g-2 g-md-3 mb-0">

                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="First Name" id="first_name.759a675b" />
                                        </dt>
                                        <dd className="mb-0">
                                            {studentWithDetails?.Firstname}
                                        </dd>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Last Name" id="last_name.029e7273" />
                                        </dt>
                                        <dd className="mb-0">
                                            {studentWithDetails?.Lastname}
                                        </dd>
                                    </Col>

                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Email" id="email.500d9e86" />
                                        </dt>
                                        <dd className="text-wrap mb-0">
                                            {studentWithDetails?.Email}
                                        </dd>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Username" id="username.dceab582" />
                                        </dt>
                                        <dd className="text-wrap mb-0">
                                            {studentWithDetails?.Username}
                                        </dd>
                                    </Col>

                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Country" id="country.cfeb54cc" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails?.Country?.Name}
                                        </dd>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="State/Province" id="stateprovince.d2dfa3c8" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails?.State?.Name ?? <FormattedMessage defaultMessage="Not Applicable" id="not_applicable.a01fc143" />}
                                        </dd>
                                    </Col>

                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Last Login" id="last_login.0b7d4377" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails?.LastAccessDateString ?? 'Never'}
                                        </dd>
                                    </Col>
                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Date Created" id="date_created.85efe6f3" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails?.RegistrationDateString}
                                        </dd>
                                    </Col>

                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Student Number" id="student_number.4ed64306" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails?.StudentNumber ?? '-----'}
                                        </dd>
                                    </Col>
                                    {studentWithDetails.FeatureRingId <= 20 && (
                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Beta Access" id="beta_access.367c484f" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails && studentWithDetails?.FeatureRingId > 0 ? <i className="fa-solid fa-check text-success fa-lg"></i> : <i className="fa-solid fa-xmark text-danger fa-lg"></i>}
                                        </dd>
                                    </Col>
                                    )}

                                    <Col md={6} xs={12}>
                                        <dt>
                                            <FormattedMessage defaultMessage="Accessibility" id="accessibility.0c8f244d" />
                                        </dt>
                                        <dd className="text-muted mb-0">
                                            {studentWithDetails?.IsAda ? <i className="fa-solid fa-check text-success fa-lg"></i> : <i className="fa-solid fa-xmark text-danger fa-lg"></i>}
                                        </dd>
                                    </Col>

                                    {isSysAdmin && (
                                        <>
                                            <Col md={6} xs={12}>
                                                <dt>
                                                    {<FormattedMessage defaultMessage="Secret Question" id="secret_question.f288b470" />}
                                                </dt>
                                                <dd className="text-wrap mb-0">
                                                    {secretQuestion?.Question ?? '-----'}
                                                </dd>
                                            </Col>
                                            <Col md={6} xs={12}>
                                                <dt>
                                                    {<FormattedMessage defaultMessage="Secret Answer" id="secret_answer.e151e11f" />}
                                                </dt>
                                                <dd className="text-wrap mb-0">
                                                    {secretQuestion?.Answer ?? '-----'}
                                                </dd>
                                            </Col>
                                        </>
                                    )}
                                </Row>
                            </Col >
                            {permissions.ViewPII && (
                                <Col md={8} xs={12}>
                                    { piiClaims.length > 0 &&
                                        (
                                            <Row as="dl" className="g-2 g-md-3 mb-0">
                                                {orderPiiClaims(piiClaims).map(claim => (
                                                    <Col key={claim.ClaimType} md={6} xs={12}>
                                                        <dt>
                                                            {claim.Name}
                                                        </dt>
                                                        <dd className="text-wrap mb-0">
                                                            {formatPiiClaimValue(claim)}
                                                        </dd>
                                                    </Col>
                                                ))}
                                            </Row>
                                        )}
                                </Col>
                            )}
                        </Row >

                        <GMetrixDialogConfirm ref={gmetrixConfirmRef} />
                        <GMetrixDialogShell ref={gmetrixDialogShellRef} />
                        <ResetPasswordDialog
                            onError={() => {

                                createToast(formatMessage({ id: 'password_reset_unsuccessfully.b693ad7f', defaultMessage: 'Password Reset Unsuccessfully' }), 'danger');
                                setState({ openModal: false });

                            }}
                            onSuccess={() => {

                                createToast(formatMessage({ id: 'password_reset_successfully.4d5010e3', defaultMessage: 'Password Reset Successfully' }), 'success');
                                setState({ openModal: false });

                            }}
                            entities={{ data: [studentWithDetails], type: 'students' }}
                            onCancel={() => setState({ openModal: false })}
                            show={openModal}
                        />

                        <GMetrixDialog
                            onCancel={() => {

                                const details = studentWithDetails;
                                setFormDetails({
                                    accessibility: details?.IsAda ?? false,
                                    betaAccess: details && details?.FeatureRingId > 0,
                                    country: details?.Country,
                                    email: details?.Email,
                                    firstName: details?.Firstname,
                                    fullName: details?.Fullname,
                                    lastName: details?.Lastname,
                                    state: details?.State,
                                    studentNumber: details?.StudentNumber,
                                    username: details?.Username
                                });

                                // Discard any unsaved PII edits by restoring the loaded claim values.
                                const restoredPiiValues: Record<string, string> = {};
                                piiClaims.forEach(claim => {

                                    restoredPiiValues[claim.ClaimType] = claim.Value ?? '';

                                });

                                setState({ piiFormValues: restoredPiiValues, showEditModal: false });

                            }}
                            actionButtonClick={editStudentDetailsOnClick}
                            actionButtonDisabled={verifyInput()}
                            actionButtonText={formatMessage({ id: 'save.d44082ae', defaultMessage: 'Save' })}
                            dialogLabel={formatMessage({ id: 'edit_details.429ca491', defaultMessage: 'Edit Details' })}
                            disabled={loading}
                            show={showEditModal}
                            size="lg"
                        >
                            <Alert dismissible onClose={clearError} show={!!error} variant={'danger'}>
                                {error}
                            </Alert>

                            {StudentForm()}
                        </GMetrixDialog>

                    </>
                )
            }
        </>
    );

}
